import { Combobox as BaseCombobox } from '@base-ui/react/combobox';
import type { ComponentProps } from 'react';
import { CheckIcon, ChevronDownIcon, CrossIcon } from '../lib/icons.tsx';
import { LAYER } from '../lib/layer.ts';
import { cx, styled } from '../lib/styled.tsx';
import {
  controlSurface,
  groupLabelSurface,
  itemChoice,
  itemSurface,
  popupSeparator,
  popupSurface,
  popupTransition,
} from '../lib/surfaces.ts';

const popup =
  `${popupSurface} ${popupTransition} ` +
  'min-w-[var(--anchor-width)] max-h-[var(--available-height)] p-1';

function Icon({ children, className, ...props }: ComponentProps<typeof BaseCombobox.Icon>) {
  return (
    <BaseCombobox.Icon className={cx('flex shrink-0 text-ink-muted', className)} {...props}>
      {children ?? <ChevronDownIcon className="size-4" />}
    </BaseCombobox.Icon>
  );
}

function Clear({ children, className, ...props }: ComponentProps<typeof BaseCombobox.Clear>) {
  return (
    <BaseCombobox.Clear
      className={cx(
        'flex size-6 shrink-0 items-center justify-center rounded-control text-ink-muted',
        'hover:bg-surface-muted hover:text-ink',
        className,
      )}
      {...props}
    >
      {children ?? <CrossIcon className="size-3.5" />}
    </BaseCombobox.Clear>
  );
}

function ItemIndicator({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseCombobox.ItemIndicator>) {
  return (
    <BaseCombobox.ItemIndicator
      className={cx('flex size-4 shrink-0 items-center justify-center text-brand-600', className)}
      {...props}
    >
      {children ?? <CheckIcon className="size-3.5" />}
    </BaseCombobox.ItemIndicator>
  );
}

function ChipRemove({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseCombobox.ChipRemove>) {
  return (
    <BaseCombobox.ChipRemove
      className={cx(
        'flex size-4 items-center justify-center rounded-full hover:bg-ink/10',
        className,
      )}
      {...props}
    >
      {children ?? <CrossIcon className="size-3" />}
    </BaseCombobox.ChipRemove>
  );
}

/**
 * Base UI keeps these two mounted at all times so screen readers announce their
 * changes reliably — they must not be hidden or conditionally rendered. That
 * makes their padding the caller's problem: put it on an inner element, or an
 * "empty" message that is not showing still reserves space in the popup.
 */
function Status({ children, className, ...props }: ComponentProps<typeof BaseCombobox.Status>) {
  return (
    <BaseCombobox.Status className={cx('', className)} {...props}>
      <div className="px-2.5 py-1.5 text-xs text-ink-muted">{children}</div>
    </BaseCombobox.Status>
  );
}

function Empty({ children, className, ...props }: ComponentProps<typeof BaseCombobox.Empty>) {
  return (
    <BaseCombobox.Empty className={cx('', className)} {...props}>
      <div className="px-2.5 py-6 text-center text-sm text-ink-muted">{children}</div>
    </BaseCombobox.Empty>
  );
}

/**
 * A text input that filters a list, for choosing from more options than a
 * {@link Select} can reasonably show. `Chips` turns it into a multi-select.
 *
 * Reach for {@link Autocomplete} instead when the list only suggests and the
 * typed text is itself a valid answer.
 */
export const Combobox = {
  /** State only — renders no element of its own. */
  Root: BaseCombobox.Root,
  Label: styled(BaseCombobox.Label, 'text-sm font-medium text-ink'),
  InputGroup: styled(
    BaseCombobox.InputGroup,
    'flex w-full flex-wrap items-center gap-1.5 rounded-control border border-border-subtle bg-surface px-2 ' +
      'focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/25 ' +
      'data-invalid:border-danger data-invalid:ring-danger/20 ' +
      'data-disabled:bg-surface-muted data-disabled:opacity-60',
  ),
  Input: styled(
    BaseCombobox.Input,
    `${controlSurface} h-9 min-w-24 flex-1 border-0 bg-transparent px-1 ring-0 focus-visible:ring-0`,
  ),
  Trigger: styled(BaseCombobox.Trigger, 'flex shrink-0 items-center outline-none'),
  Icon,
  Clear,
  Value: styled(BaseCombobox.Value, 'truncate'),
  Chips: styled(BaseCombobox.Chips, 'flex flex-wrap items-center gap-1'),
  Chip: styled(
    BaseCombobox.Chip,
    'flex items-center gap-1 rounded-control bg-surface-muted px-2 py-0.5 text-xs text-ink ' +
      'data-highlighted:bg-brand-100 data-highlighted:text-brand-900',
  ),
  ChipRemove,
  Portal: BaseCombobox.Portal,
  Backdrop: styled(BaseCombobox.Backdrop, 'fixed inset-0'),
  Positioner: styled(BaseCombobox.Positioner, `${LAYER.popup} outline-none`, { sideOffset: 6 }),
  Popup: styled(BaseCombobox.Popup, popup),
  Arrow: styled(BaseCombobox.Arrow, ''),
  Status,
  Empty,
  List: styled(BaseCombobox.List, 'max-h-[inherit] overflow-y-auto outline-none'),
  Row: styled(BaseCombobox.Row, 'flex'),
  Item: styled(BaseCombobox.Item, `${itemSurface} justify-between ${itemChoice}`),
  ItemIndicator,
  Separator: styled(BaseCombobox.Separator, popupSeparator),
  Group: styled(BaseCombobox.Group, ''),
  GroupLabel: styled(BaseCombobox.GroupLabel, groupLabelSurface),
  /** Renders the filtered items; virtualises long lists. */
  Collection: BaseCombobox.Collection,
};
